import { formatCopy, type NamespaceKey } from '@/i18n/ns';
import type pageSuggestionCopy from '@/i18n/ns/page-suggestion-card';
import { knowledgePagesApi } from '@/workspace/knowledge-pages/api';

// The review card for suggest_page_edits names the page in words and shows the
// change as a diff. Page ids stay out of the card: titles come from the
// knowledge tree this person can read. The read confers no authority; the
// server resolves and checks the page again when the suggestion is sent.

type Args = Record<string, unknown>;
export type SuggestionCopy = Readonly<Record<NamespaceKey<typeof pageSuggestionCopy>, string>>;
type Pages = Pick<typeof knowledgePagesApi, 'tree' | 'view'>;

/** Titles of the pages a suggestion names; null when the page cannot be read. */
export type SuggestionTitles = { page: string | null; parent: string | null };

export type PageSuggestionView = {
  /** The target in plain words, or null for an action this card does not know. */
  heading: string | null;
  /** "Rename X to Z" when an edit gives the page a new title. */
  rename: string | null;
  /** The suggestion changes page text: an edit's edits, or a new page's text. */
  textChange: boolean;
  /** The card has a Changes view (text, or a new title). */
  hasChanges: boolean;
  /** What the Draft tab shows: the reason, and a new page's text. */
  draftArgs: Args;
  send: string;
  footer: string;
};

const text = (value: unknown) => (typeof value === 'string' ? value : null);

/** A suggested new page's text: the markdown of its replace_page edit. */
export function createdPageMarkdown(args: Args): string | null {
  if (args.action !== 'create' || !Array.isArray(args.edits)) return null;
  const edit: unknown = args.edits.find(
    (item: unknown) =>
      Boolean(item) &&
      typeof item === 'object' &&
      (item as { type?: unknown }).type === 'replace_page',
  );
  return edit ? text((edit as { markdown?: unknown }).markdown) : null;
}

const editsText = (args: Args) =>
  args.action === 'edit' && Array.isArray(args.edits) && args.edits.length > 0;

/**
 * Reads the titles in one tree request. A reference the tree does not list
 * (a slug, or a page gone since) is tried once through the page view, which
 * resolves both. A parent of null, or none for a new page, is the root page.
 */
export async function resolveSuggestionTitles(
  args: Args,
  pages: Pages = knowledgePagesApi,
): Promise<SuggestionTitles> {
  const tree = await pages.tree().catch(() => null);
  const known = new Map<string, string>(tree?.pages.map((page) => [page.id, page.title] as const));
  const title = async (ref: string | null) => {
    if (ref === null) return null;
    const listed = known.get(ref);
    if (listed !== undefined) return listed;
    return pages.view(ref).then(
      (view) => view.page.title,
      () => null,
    );
  };
  const places = args.action === 'create' || args.action === 'move';
  const [page, parent] = await Promise.all([
    args.action === 'create' ? null : title(text(args.pageId)),
    places ? title(text(args.parentId) ?? tree?.rootId ?? null) : null,
  ]);
  return { page, parent };
}

function headingFor(
  args: Args,
  titles: SuggestionTitles,
  rename: string | null,
  copy: SuggestionCopy,
) {
  const page = titles.page ?? copy.unavailable;
  const parent = titles.parent ?? copy.unavailable;
  if (args.action === 'create')
    return formatCopy(copy.create, { title: text(args.title) || copy.untitled, parent });
  if (args.action === 'move') return formatCopy(copy.move, { page, parent });
  if (args.action === 'archive') return formatCopy(copy.archive, { page });
  if (args.action === 'edit') return rename ?? titles.page ?? copy.unavailableTitle;
  return null;
}

/** The card's readable view; `titles` null means they are still loading. */
export function pageSuggestionView(
  args: Args,
  titles: SuggestionTitles | null,
  copy: SuggestionCopy,
): PageSuggestionView {
  const newTitle = args.action === 'edit' ? text(args.title) : null;
  const rename =
    newTitle === null
      ? null
      : !titles
        ? copy.loading
        : newTitle !== titles.page
          ? formatCopy(copy.rename, {
              page: titles.page ?? copy.unavailable,
              title: newTitle || copy.untitled,
            })
          : null;
  const markdown = createdPageMarkdown(args);
  const textChange = editsText(args) || markdown !== null;
  return {
    heading: titles ? headingFor(args, titles, rename, copy) : copy.loading,
    rename,
    textChange,
    hasChanges: textChange || newTitle !== null,
    draftArgs: {
      ...(typeof args.reason === 'string' ? { reason: args.reason } : {}),
      ...(markdown !== null ? { markdown } : {}),
    },
    send: copy.send,
    footer: copy.pendingFooter,
  };
}
